
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Zap, Target, Brain, Users, ArrowRight, CheckCircle, Star, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import AILeadEngineHero from '@/components/ai-lead-engine/AILeadEngineHero';
import AILeadEngineIntro from '@/components/ai-lead-engine/AILeadEngineIntro';
import AILeadEngineProcess from '@/components/ai-lead-engine/AILeadEngineProcess';
import AILeadEngineBetaForm from '@/components/ai-lead-engine/AILeadEngineBetaForm';
import AILeadEngineTrust from '@/components/ai-lead-engine/AILeadEngineTrust';
import PageSEO from '@/components/seo/PageSEO';

const AILeadEngine = () => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI Lead Engine",
      "description": "AI-gedreven leadgeneratie voor B2B bedrijven. Meer afspraken, minder koud bellen, slimmere sales.",
      "provider": {
        "@type": "Organization",
        "name": "Afsprakenmaker",
        "url": "https://afsprakenmaker.be"
      },
      "serviceType": "AI Lead Generation",
      "areaServed": "Belgium",
      "availableChannel": {
        "@type": "ServiceChannel",
        "serviceUrl": "https://afsprakenmaker.be/ai-lead-engine",
        "serviceSmsNumber": "+32 485 96 61 83"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "Hoe werkt de AI Lead Engine van Afsprakenmaker?",
      "description": "Van anonieme websitebezoeker naar gekwalificeerde afspraak in 6 slimme stappen",
      "step": [
        {
          "@type": "HowToStep",
          "position": 1,
          "name": "Website traffic activeren",
          "text": "We voorzien een slimme landingspagina of verrijken jouw bestaande site. We boosten gericht verkeer via Google Ads of andere kanalen."
        },
        {
          "@type": "HowToStep",
          "position": 2,
          "name": "Realtime bezoekerherkenning",
          "text": "Dankzij slimme tracking (Leadinfo) zien we welke bedrijven je website bezoeken. Surfgedrag wordt geanalyseerd (volledig GDPR-compliant)."
        },
        {
          "@type": "HowToStep",
          "position": 3,
          "name": "Data-verrijking & signalen",
          "text": "Automatische verrijking van bedrijfs- en contactdata. Sales intelligence opgebouwd in de achtergrond."
        },
        {
          "@type": "HowToStep",
          "position": 4,
          "name": "Gepersonaliseerde outreach",
          "text": "Slimme e-mailcampagnes en LinkedIn-interacties worden getriggerd. Volledig afgestemd op het gedrag en de interesses van je bezoekers."
        },
        {
          "@type": "HowToStep",
          "position": 5,
          "name": "Warme leads voor je call agents",
          "text": "Call agents ontvangen enkel verrijkte, gekwalificeerde leads. Met alle info: surfgedrag, contactmomenten, interesses, bedrijfssignalen."
        },
        {
          "@type": "HowToStep",
          "position": 6,
          "name": "Resultaat",
          "text": "Meer afspraken. Minder werk. 100% gefocust op echte kansen."
        }
      ]
    }
  ];

  return (
    <>
      <PageSEO
        title="AI Lead Engine (BETA) | Afsprakenmaker - De toekomst van B2B prospectie"
        description="Ontdek onze AI Lead Engine: meer afspraken, minder koud bellen, slimmere sales. Word BETA-tester en profiteer als eerste van AI-gedreven leadgeneratie."
        keywords="AI leadgeneratie B2B, slimme prospectie, warme outbound, moderne leadgeneratie, AI lead engine, BETA programma"
        canonicalUrl="/ai-lead-engine"
        structuredData={structuredData}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <AILeadEngineHero />

        {/* Intro Section */}
        <AILeadEngineIntro />

        {/* Process Overview */}
        <AILeadEngineProcess />

        {/* BETA Form Section */}
        <AILeadEngineBetaForm />

        {/* Trust Building Footer */}
        <AILeadEngineTrust />

        {/* Bottom CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-brand-blue to-brand-darkBlue rounded-2xl p-12 text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Vragen over de AI Lead Engine?
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Neem direct contact op voor meer informatie of een persoonlijk gesprek
              </p>
              <Button 
                size="xl" 
                variant="outline"
                className="bg-white text-brand-blue hover:bg-gray-100 border-white"
                asChild
              >
                <a href="/contact">
                  <Phone className="mr-2 h-5 w-5" />
                  Neem contact op
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Sticky CTA at bottom for mobile */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40 md:hidden">
          <Button 
            className="w-full" 
            size="lg" 
            variant="highlight"
            scrollToId="beta-form"
          >
            <Zap className="mr-2 h-4 w-4" />
            Vraag toegang tot de BETA
          </Button>
        </div>
      </div>
    </>
  );
};

export default AILeadEngine;
